import { z, ZodOptional, ZodString, ZodType } from 'zod';
import { RatingAndReviewFieldID } from './RatingsAndReviewsFormValue';

type FieldOptions = {
    minLength?: number;
    minLengthMessage?: string;
    maxLength?: number;
    maxLengthMessage?: string;
    requiredMessage?: string;
};

type StringFieldOptions = {
    minLength?: number;
    minLengthMessage?: string;
    maxLength?: number;
    maxLengthMessage?: string;
    requiredMessage?: string;
};

const stringField = (isRequired: boolean, options: StringFieldOptions = {}): ZodString | ZodOptional<ZodString> => {
    const { minLength, minLengthMessage, maxLength, maxLengthMessage, requiredMessage } = options;

    let schema = z.string();

    if (isRequired) {
        schema = schema.min(1, { message: requiredMessage });
    }

    if (minLength && minLength > 1) {
        schema = schema.min(minLength, { message: minLengthMessage });
    }

    if (maxLength) {
        schema = schema.max(maxLength, { message: maxLengthMessage });
    }

    if (!isRequired) {
        return schema.optional();
    }

    return schema;
};

const numberStringField = (isRequired: boolean, options: FieldOptions = {}) => {
    const base = z.string({ required_error: options.requiredMessage }).refine((val) => val.trim() !== '', {
        message: options.requiredMessage,
    });

    return isRequired ? base : base.optional();
};

export const generateFormSchema = (fields: Array<{ id: string; type: string; isRequired: boolean }>) => {
    const shape: Record<string, z.ZodTypeAny> = {};

    fields.forEach(({ id, type, isRequired }) => {
        switch (type) {
            case 'TEXT':
                shape[id] = (() => {
                    if (id === RatingAndReviewFieldID.TITLE) {
                        return stringField(isRequired, {
                            minLength: 1,
                            maxLength: 50,
                            maxLengthMessage: 'title.errorMaxLength',
                        });
                    }

                    if (id === RatingAndReviewFieldID.CONTENT) {
                        return stringField(isRequired, {
                            minLength: 1,
                            maxLength: 1000,
                            maxLengthMessage: 'label.errorMaxLength',
                            requiredMessage: 'label.errorRequired',
                        });
                    }

                    return stringField(isRequired, {
                        minLength: 1,
                        requiredMessage: 'label.errorRequired',
                    });
                })();
                break;

            case 'NUMBER':
                shape[id] = numberStringField(isRequired, {
                    requiredMessage: 'label.errorRequired',
                });
                break;

            case 'SELECT':
                shape[id] = stringField(isRequired, {
                    requiredMessage: 'label.errorRequired',
                    minLengthMessage: 'label.errorMinLength',
                    minLength: 1,
                });
                break;

            default:
                shape[id] = z.any();
        }
    });

    return z.object(shape);
};

export type RatingsAndReviewsFormData = z.infer<ReturnType<typeof generateFormSchema>>;

export type RatingsAndReviewsFormState = {
    data: z.infer<ReturnType<typeof generateFormSchema>>;
    errors: undefined | Record<string, string[]>;
    success: boolean;
};
